<template>
  <div class="row gx-lg-5 gx-md-0">
    <div class="col-lg-9 col-md-12">
      <section>
        <investments-chart :fund-data="fundsData" :data-loading="loadingFunds"></investments-chart>
      </section>
			<section class="mt-5" v-if="!loadingFunds">
				<div class="d-flex justify-content-between">
					<h3>Fund Performance</h3>
					<p class="text-alternate">As of <format-date :date="fundDataAsOf" to-format="MM/dd/yyyy"/></p>
				</div>
				<!-- pill-control
					:nav-options="[{name: 'All', value: null}, {name: 'Current', value: 'current'}, {name: 'Exited', value: 'exited'}]"
					@pill-nav-clicked="pillNavClicked"/ -->
				<EasyDataTable
					:alternating="true"
					:headers="fundTableHeadings"
					table-class-name="custom-table"
					:rows-per-page="rows_per_page"
					:hide-footer="hideFooter"
					:items="fundTableItems">
					<template #item-fund="{fund, fundId}">
						<span><router-link :to="{name: 'FundInvestment', params: {fundId}}">{{fund}}</router-link></span>
					</template>
				</EasyDataTable>
			</section>
				
			<section v-if="loadingFunds">
				<TablePlaceholder/>
			</section>
    </div>
    <div class="col-3 d-md-none d-lg-block border-start">
      <investments-sidebar
				sidebar-title="Active Investments"
				:investment-data="sidebarInvestmentData"/>
    </div>
  </div>
</template>

<script type="text/javascript">
  import InvestmentsChart from './InvestmentsChart'
	import InvestmentsSidebar from './InvestmentsSidebar'
	// put back when we have filtering by fund status import PillControl from '@/components/controls/PillControl'
  import {formatDollars} from "@/utils/currency_utils";
	import {formatPercentage} from "@/utils/percent_utils";
	import FormatDate from '@/views/utils/FormatDate'
  import FundDataService from "@/services/FundDataService";
	import {formatNumber} from "@/utils/number_utils";
	import {InvestmentsApi} from '@/api/investment_api'
	import {FinancialDataApi, eavField} from '@/api/financial_data_api'
	import {DateTime} from 'luxon'
	import TablePlaceholder from '../skeletonLoaders/TablePlaceholder.vue';

	export default {
		name: 'InvestmentsView',
		inheritAttrs: false,

		mixins: [],

		components: {
			InvestmentsChart,
			InvestmentsSidebar,
			//PillControl,
			FormatDate,
			TablePlaceholder
		},

		filters: {
		},

		provide: function() {
			return {
			}
		},

		inject: [],

		props: [],

		setup() {
		},

		data() {
			return {
				fundTableHeadings: [
					{text: 'Fund', value: 'fund'},
					{text: 'Invested', value: 'invested'},
					{text: 'Fees', value: 'fees'},
					{text: 'Expenses', value: 'expenses'},
					{text: 'Available', value: 'available'},
					{text: 'Gross IRR', value: 'grossIRR'},
					{text: 'Net IRR', value: 'netIRR'},
					{text: 'Gross MOIC', value: 'grossMOIC'},
					{text: 'Net MOIC', value: 'netMOIC'}
				],

				fundsData: [],
				sumRows: [],
				fundDataAsOf: null,
				loadingFunds: false,
				activeInvestments: [],
				rows_per_page: 25
			}
		},

		computed: {
			asOfDateString() {
				return this.fundDataAsOf || ''
			},

			fundTableItems() {
				const rows = []
				this.fundsData.forEach((fund) => {
					if (fund.asOf) this.fundDataAsOf = DateTime.fromISO(fund.asOf).toJSDate()
					rows.push({
						fundId:    fund.guid,
						fund:      fund.name,
						invested:  formatDollars(fund.funds_data?.invested, {scale: 'mm'}),
						fees:      formatDollars(fund.funds_data?.fees, {scale: 'mm'}),
						expenses:  formatDollars(fund.funds_data?.expenses, {scale: 'mm'}),
						available: formatDollars(fund.funds_data?.available, {scale: 'mm'}),
						grossIRR:  fund.fund_performance ? formatPercentage(fund.fund_performance.grossIRR) : '-',
						netIRR:    fund.fund_performance ? formatPercentage(fund.fund_performance.netIRR) : '-',
						grossMOIC: fund.fund_performance?.grossMOIC ? formatNumber(fund.fund_performance.grossMOIC) : '-',
						netMOIC:   fund.fund_performance?.netMOIC ? formatNumber(fund.fund_performance.netMOIC) : '-'
					})
				})
				return rows
			},

			sidebarInvestmentData() {
				return this.activeInvestments.map(inv => ({
					guid: inv.guid,
					name: inv.name,
					overview: inv.overview,
					mostRecentFinancials: inv.mostRecentFinancials
				}))
			},

			hideFooter() {
				return this.fundTableItems.length <= this.rows_per_page
			}
		},

		watch: {

		},

		created() {
		},

		mounted() {
			return Promise.all([
				this.loadFundTable(),
				this.loadActiveInvestments()
			])
		},

		methods: {
			async loadActiveInvestments() {
				const resp = await InvestmentsApi.getInvestments({'$filter': "status eq 'Active'", '$top': 100})
				const investments = resp.value || []

				if (investments.length > 0) {
					const investmentIds = investments.map(inv => inv.guid)
					const batchData = await FinancialDataApi.batchGetInvestmentFinancials(
						investmentIds,
						['investment_financials', 'investment_overview']
					)

					investments.forEach(inv => {
						const invData = batchData[inv.guid] || {}
						const financialRecords = invData['investment_financials'] || []
						const overviewRecords = invData['investment_overview'] || []

						const latestFields = {}
						if (financialRecords.length > 0) {
							const latest = financialRecords.reduce((best, r) => {
								const bestDate = best.fields?.find(f => f.fieldName === 'period_date')?.value || ''
								const rDate = r.fields?.find(f => f.fieldName === 'period_date')?.value || ''
								return rDate > bestDate ? r : best
							}, financialRecords[0])
							latest.fields?.forEach(f => { latestFields[f.fieldName] = f.value })
						}
						inv.mostRecentFinancials = {
							data: {
								'LTM Revenue': latestFields.ltm_revenue ?? null,
								'LTM EBITDA': latestFields.ltm_ebitda ?? null
							}
						}

						if (overviewRecords.length > 0) {
							const overviewFields = {}
							overviewRecords[0].fields?.forEach(f => { overviewFields[f.fieldName] = f.value })
							inv.overview = {
								asOf: overviewRecords[0].lastUpdated ?? null,
								'Financial Information (At Entry)': {
									'LTM Revenue': overviewFields.entry_ltm_revenue ?? null,
									'LTM EBITDA': overviewFields.entry_ltm_ebitda ?? null
								}
							}
						}
					})
				}

				this.activeInvestments = investments
			},

			async loadFundTable() {
				this.loadingFunds = true
				try {
					const funds = await FundDataService.loadFunds()
					const [perfResults, fundsDataResults] = await Promise.all([
						Promise.all(funds.map(f => FinancialDataApi.getFundFinancialsByType(f.guid, 'fund_performance').then(r => ({ guid: f.guid, records: r })))),
						Promise.all(funds.map(f => FinancialDataApi.getFundFinancialsByType(f.guid, 'fund_funds_data').then(r => ({ guid: f.guid, records: r }))))
					])

					const perfByGuid = {}
					perfResults.forEach(p => { perfByGuid[p.guid] = p.records })

					const fundsDataByGuid = {}
					fundsDataResults.forEach(f => { fundsDataByGuid[f.guid] = f.records })

					this.fundsData = funds.map(fund => {
						const pr = perfByGuid[fund.guid] || []
						const fdr = fundsDataByGuid[fund.guid] || []
						return {
							...fund,
							asOf: fdr[0]?.fields?.find(f => f.fieldName === 'as_of')?.value ?? null,
							fund_performance: pr.length ? {
								grossIRR:  eavField(pr, 'gross_irr'),
								netIRR:    eavField(pr, 'net_irr'),
								grossMOIC: eavField(pr, 'gross_moic'),
								netMOIC:   eavField(pr, 'net_moic')
							} : null,
							funds_data: fdr.length ? {
								invested:  eavField(fdr, 'invested'),
								fees:      eavField(fdr, 'fees'),
								expenses:  eavField(fdr, 'expenses'),
								available: eavField(fdr, 'available')
							} : null
						}
					})
				} catch (e) {
					console.error('error loading fund performance data', e)
				} finally {
					this.loadingFunds = false
				}
			},
			pillNavClicked() {
			}
		}
	}
</script>

<style lang="scss">
</style>
